/**
 * Finds the index of an object in an array based on the `name` property.
 *
 * @param {Array<Permission>} array - The array of objects to search.
 * @param {string} name - The name to find in the array.
 * @returns {number} - The index of the object if found, otherwise `-1`.
 */
const indexFinder = (array, name) => {
    return array.findIndex(item => item.name === name);
}
/**
 * @typedef {Object} Permission
 * @property {string} name - The name of the permission.
 * @property {Permission[]} [children] - Optional nested permissions.
 */

/**
 * Checks whether a user has the required permissions by navigating through a hierarchical permission structure.
 *
 * @param {Permission[]} userPerms - The user's permission hierarchy.
 * @param {...string} permissionLevels - The required permission levels to check, in a nested sequence.
 * @returns {boolean} - `true` if the user has all the required permissions, otherwise `false`.
 *
 * @example
 * const userPermissions = [
 *   { name: "Access Management", children: [{ name: "Role Management", children: [{ name: "Create" }] }] },
 *   { name: "General Services", children: [{ name: "New Connection" }] }
 * ];
 *
 * CheckPermissions(userPermissions, "Access Management", "Role Management", "Create"); // Returns true
 * CheckPermissions(userPermissions, "General Services", "New Connection", "Create"); // Returns false
 */
const CheckPermissions = (userPerms, ...permissionLevels) => {
    let currentPermissions = userPerms;

    for (let level of permissionLevels) {
        // Find the permission at the current level
        let permissionIndex = indexFinder(currentPermissions, level);
        if (permissionIndex === -1) {
            return false; // Permission not found
        }
        // Move to the next level's children if they exist
        currentPermissions = currentPermissions[permissionIndex].children || [];
    }

    return true; // All permissions found
}

export default CheckPermissions;